import type { DynamicStructure } from "../../utils/interpolate";
import { normalizeRepeater } from "../../utils/interpolate/core/normalizeRepeater";
import { findEntryFieldsByType } from "../../utils/entryFields";
import { REPEATER_FIELD_TYPE, type HighlightItem, type HighlightsFieldMap } from "./types";

export type ResolveHighlightsParams = {
  /** Registro da página de item, entregue pelo Node.tsx / wrapper do builder. */
  dynamicStructure?: DynamicStructure;
  /** Nome do campo escolhido no sidebar. Vazio = descobrir pelo tipo. */
  highlightsField?: string;
  /** Qual sub-campo do repetidor alimenta cada parte do card. */
  fieldMap?: HighlightsFieldMap;
  /** Teto de itens exibidos. `0`/ausente = todos. */
  maxItems?: number;
};

const read = (values: Record<string, string>, key?: string): string =>
  key && key.trim() !== "" ? (values[key] ?? "") : "";

/**
 * Resolve os itens do card a partir do campo `repeater` do registro.
 *
 * Descoberta do campo, nesta ordem:
 * 1. `highlightsField` (o Champion escolheu, porque a coleção tem mais de um
 *    campo do tipo);
 * 2. o ÚNICO campo `repeater` do registro, via `_metadata.fields` — nunca por
 *    nome, ver `entryFields.ts`. Com mais de um repetidor e nenhum escolhido,
 *    não se descobre por tipo: qual seria o "primeiro" varia por registro
 *    (ordem do `_metadata.fields`), e o canvas pede para escolher o campo.
 *
 * O repetidor não tem forma fixa: quem diz qual sub-campo vira imagem, título,
 * descrição resumida e completa é o `fieldMap`, montado no sidebar. É isso que
 * permite o mesmo bloco servir coleções com nomenclaturas diferentes.
 *
 * Item sem imagem ou sem título é descartado — sem eles o card não tem o que
 * exibir. A ordem é a de cadastro e `maxItems` corta o fim.
 */
export const resolveHighlightItems = ({
  dynamicStructure,
  highlightsField,
  fieldMap,
  maxItems = 0,
}: ResolveHighlightsParams): HighlightItem[] => {
  const entry = dynamicStructure?.item;
  if (!entry) return [];

  let fieldName: string | undefined;
  if (highlightsField && highlightsField.trim() !== "") {
    fieldName = highlightsField.trim();
  } else {
    const candidates = findEntryFieldsByType({ entry, type: REPEATER_FIELD_TYPE });
    fieldName = candidates.length === 1 ? candidates[0].name : undefined;
  }

  if (!fieldName) return [];

  const items = normalizeRepeater(entry[fieldName]).flatMap((item) => {
    const image = read(item.values, fieldMap?.image);
    const title = read(item.values, fieldMap?.title);
    if (image.trim() === "" || title.trim() === "") return [];

    return [
      {
        id: item.id,
        image,
        title,
        description: read(item.values, fieldMap?.description),
        extendedDescription: read(item.values, fieldMap?.extendedDescription),
      },
    ];
  });

  // `0` (e qualquer valor não positivo) significa "todos".
  if (!Number.isFinite(maxItems) || maxItems <= 0) return items;

  return items.slice(0, maxItems);
};

/** Itens que abrem modal — os que têm descrição completa preenchida. */
export const filterModalItems = (items: HighlightItem[]): HighlightItem[] =>
  items.filter((item) => (item.extendedDescription || "").trim() !== "");
